import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import type { PDPTechSpecsFeature } from './PDPTechSpecsFeatures';
import PDPTechSpecsFeatures from './PDPTechSpecsFeatures';
import type { PDPWhatsIncludedProps } from './PDPWhatsIncluded';
import PDPWhatsIncluded from './PDPWhatsIncluded';

export interface PDPTechSpecsSectionProps {
  readonly whatsIncludedProps: PDPWhatsIncludedProps;
  readonly features: PDPTechSpecsFeature[];
  readonly sectionTitle: MessageKey;
  readonly variant?: 'dark' | 'light';
}

const PDPTechSpecsSection = ({
  whatsIncludedProps,
  features,
  sectionTitle,
  variant = 'dark',
}: PDPTechSpecsSectionProps) => {
  const t = useTranslations();

  return (
    <LayoutGrid
      className={cx('relative z-10 gap-y-10 pt-20 lg:pt-0', {
        'bg-gray-450 text-sandstone-200': variant === 'dark',
        'mx-4 rounded-[40px] bg-gradient-to-r from-[#E8E1DA] to-[#DDD5CD] text-sandstone-500':
          variant === 'light',
      })}
      data-cy="pdp-tech-specs"
      Element="section"
    >
      <GridItem
        className="lg:mr-4 lg:pb-20"
        colEnd={{ sm: 'main', md: 'main', lg: 10 }}
        colStart={{ sm: 'main' }}
        rowEnd={{ sm: 3 }}
        rowStart={{ sm: 1 }}
      >
        <PDPWhatsIncluded {...whatsIncludedProps} variant={variant} />
      </GridItem>
      <GridItem
        className="lg:pt-20"
        colEnd={{ sm: 'main' }}
        colStart={{ sm: 'main', md: 'main', lg: 10 }}
      >
        <Typography
          color={variant === 'dark' ? 'light' : 'dark'}
          Element="h4"
          variant="h4"
          className={cx({
            'font-normal': variant === 'light',
          })}
        >
          {t(sectionTitle)}
        </Typography>
      </GridItem>
      <GridItem
        className="pb-20"
        colEnd={{ sm: 'main' }}
        colStart={{ sm: 'main', md: 'main', lg: 10 }}
      >
        <PDPTechSpecsFeatures features={features} />
      </GridItem>
    </LayoutGrid>
  );
};

export default PDPTechSpecsSection;
